/**
 * htmlJsonMergeUtil.ts - Shared HtmlJson/DataJson merging utilities
 * Port of C# HtmlJsonMergeUtil.cs
 * Centralized JSON structure processing logic (dictionary-based, not string-based)
 * Pure ES module - works in both Node.js and browser via bundler
 *
 * Uses new HtmlJson format with type prefixes:
 * s=Html, k=JsonKey, K=JsonKeySkip, p=Placeholder, P=PlaceholderSkip,
 * h=Slotholder, H=SlotholderSkip, a=ArrayBlock, A=ArrayBlockSkip, x=SlotHtml, i=Slot, c=Conditional
 */

import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { TemplateOperation } from '../../../../Assembler/dist/model/index.js';

import { TemplateOperationKind } from '../../../../Assembler/dist/model/index.js';
// Import constants
import {
    TYPE_JSONKEY,
    TYPE_JSONKEY_SKIP,
    META_KEY,
    hasKey,
    getJsonValue,
    getKeyName,
    isTypePrefixedKey
} from './htmlJsonMergeUtil_Constants.js';

// Import conversion utilities
import { convertToString, mergeContextIntoDataJson } from './htmlJsonMergeUtil_Conversion.js';

// Re-export constants for external use
export {
    TYPE_HTML,
    TYPE_JSONKEY,
    TYPE_JSONKEY_SKIP,
    TYPE_PLACEHOLDER,
    TYPE_PLACEHOLDER_SKIP,
    TYPE_SLOTHOLDER,
    TYPE_SLOTHOLDER_SKIP,
    TYPE_ARRAYBLOCK,
    TYPE_ARRAYBLOCK_SKIP,
    TYPE_SLOTHTML,
    TYPE_SLOT,
    TYPE_CONDITIONAL,
    META_KEY,
    hasKey,
    getValue,
    getKeyName,
    isTypePrefixedKey
} from './htmlJsonMergeUtil_Constants.js';

// Re-export conversion utilities
export { convertToString, mergeContextIntoDataJson } from './htmlJsonMergeUtil_Conversion.js';

function executeWithReadOnlyContext<T>(contextJson: JsonObject | null, action: () => T): T {
    const wasReadOnly = contextJson?.isReadOnly ?? false;

    if (contextJson) {
        contextJson.isReadOnly = true;
    }

    try {
        return action();
    } finally {
        if (contextJson) {
            contextJson.isReadOnly = wasReadOnly;
        }
    }
}

// [Dafny Verified] Properties J1-J7 — Verification/dafny/src/MergeDataJsonIntoHtmlJson.dfy
/**
 * Merges DataJson into HtmlJson structure by replacing JsonKey entries
 * This is the dictionary-based version (for AssemblerJson engines)
 * For string-based template merging, use JsonMergeUtil instead
 */
export function mergeDataJsonIntoHtmlJson(
    htmlJson: JsonObject,
    dataJson: JsonObject,
    templateKey: string = '',
    currentTemplateName: string = '',
    operations: TemplateOperation[] | null = null,
    defaultReplaceHtml: string = '*****',
    insideArrayBlock: boolean = false,
    contextJson: JsonObject | null = null,
    loggedKeys: Set<string> | null = null,
    keyReplaceOps: TemplateOperation[] | null = null,
    keyMissingOps: TemplateOperation[] | null = null,
    matchedKeysAccumulator: Set<string> | null = null,
    missingKeysAccumulator: string[] | null = null
): JsonObject {
    return executeWithReadOnlyContext(contextJson, () => {
        const result = new JsonObject();

        if (loggedKeys === null) {
            loggedKeys = new Set<string>();
        }

        const isArrayBlockStructure = htmlJson.has('MainBlock') || htmlJson.has('EmptyBlock');

        let mergedDataJson: JsonObject | null = null;
        const contextHasItems = contextJson !== null && contextJson !== undefined;

        const trackOperations = operations !== null && !!templateKey && !!currentTemplateName;

        // Track keys matched (data-source casing) and missing keys (in htmlJson scan order)
        // so KeyReplace ops can be emitted in data-source order (matching Normal/PreProcess),
        // while substitution into `result` still happens in htmlJson order.
        //
        // Accumulators are SHARED across nested-dict recursion (conditional/slot blocks) and
        // emission happens only at the outermost (owning) call. This makes KeyReplace ops for
        // the whole template ordered by the top-level dataJson order — and KeyMissing ops by
        // document scan order — matching Normal's single flat pass. Emitting per nesting level
        // (bottom-up) interleaved deep-conditional keys ahead of shallow ones (e.g. PageSize
        // before TotalPages for paginated components).
        const ownsAccumulators = matchedKeysAccumulator === null;
        const matchedDataKeys: Set<string> | null = trackOperations ? (matchedKeysAccumulator ?? new Set<string>()) : null;
        const missingKeysInTemplateOrder: string[] | null = trackOperations ? (missingKeysAccumulator ?? []) : null;

        for (const [key, value] of htmlJson.entries()) {
            if (key === META_KEY) {
                result.set(key, value);
                continue;
            }

            const keyType: string = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';

            if (keyType === TYPE_JSONKEY_SKIP) {
                result.set(key, value);
            } else if (keyType === TYPE_JSONKEY) {
                const keyName = getKeyName(key);

                if (!keyName) {
                    result.set(key, value);
                    continue;
                }

                if (insideArrayBlock) {
                    result.set(key, value);
                } else if (contextJson && hasKey(contextJson, keyName)) {
                    result.set(key, convertToString(getJsonValue(contextJson, keyName)));
                    // Defer KeyReplace op emission — it happens in data-source order below.
                    matchedDataKeys?.add(keyName);
                } else if (dataJson && hasKey(dataJson, keyName)) {
                    result.set(key, convertToString(getJsonValue(dataJson, keyName)));
                    // Defer KeyReplace op emission — it happens in data-source order below.
                    matchedDataKeys?.add(keyName);
                } else if (keyName.toUpperCase().startsWith('HTMLPLACEHOLDER')) {
                    result.set(key, value);
                } else {
                    result.set(key, defaultReplaceHtml);
                    missingKeysInTemplateOrder?.push(keyName);
                }
            } else if (value.asObject() !== null) {
                if (mergedDataJson === null) {
                    if (contextHasItems) {
                        mergedDataJson = mergeContextIntoDataJson(dataJson, contextJson!);
                    } else {
                        mergedDataJson = dataJson;
                    }
                }

                // Propagate keyReplaceOps/keyMissingOps + accumulators into recursion so nested
                // templates emit KeyReplace/KeyMissing into the same staging lists. Without this,
                // nested emissions would fall back to `operations` and break canonical phase ordering.
                result.set(key, mergeDataJsonIntoHtmlJson(
                    value.asObject()!,
                    mergedDataJson,
                    templateKey,
                    currentTemplateName,
                    operations,
                    defaultReplaceHtml,
                    isArrayBlockStructure || insideArrayBlock,
                    null,
                    loggedKeys,
                    keyReplaceOps,
                    keyMissingOps,
                    matchedDataKeys,
                    missingKeysInTemplateOrder
                ));
            } else {
                result.set(key, value);
            }
        }

        // Only the outermost call flushes accumulated ops — nested calls just accumulate.
        if (!ownsAccumulators) {
            return result;
        }

        // Emit KeyReplace ops in merged-data order, mirroring Normal/PreProcess.
        // dataJson keys (in their order) → contextJson-only keys (in their order).
        // Route to keyReplaceOps/keyMissingOps when provided so the caller can interleave them
        // at the right phase position (Normal emits KeyReplace AFTER ArrayIteration); otherwise
        // fall back to the operations parameter (legacy path).
        const keyReplaceTarget = keyReplaceOps ?? operations;
        const keyMissingTarget = keyMissingOps ?? operations;

        if (trackOperations && matchedDataKeys && matchedDataKeys.size > 0 && keyReplaceTarget !== null) {
            const matchedLower = new Set<string>();
            for (const k of matchedDataKeys) matchedLower.add(k.toLowerCase());
            const emittedLower = new Set<string>();

            for (const dataKey of dataJson.keys()) {
                const dataKeyLower = dataKey.toLowerCase();
                if (matchedLower.has(dataKeyLower) && !loggedKeys.has(dataKeyLower)) {
                    loggedKeys.add(dataKeyLower);
                    keyReplaceTarget.push(new TemplateOperation({
                        templateName: templateKey,
                        kind: TemplateOperationKind.KeyReplace,
                        target: `{{$${dataKey}}}`,
                        replacedWith: `${currentTemplateName}_${dataKey}`,
                        operations: []
                    }));
                    emittedLower.add(dataKeyLower);
                }
            }
            if (contextJson) {
                for (const ctxKey of contextJson.keys()) {
                    const ctxKeyLower = ctxKey.toLowerCase();
                    if (emittedLower.has(ctxKeyLower)) continue;
                    if (matchedLower.has(ctxKeyLower) && !loggedKeys.has(ctxKeyLower)) {
                        loggedKeys.add(ctxKeyLower);
                        keyReplaceTarget.push(new TemplateOperation({
                            templateName: templateKey,
                            kind: TemplateOperationKind.KeyReplace,
                            target: `{{$${ctxKey}}}`,
                            replacedWith: `${currentTemplateName}_${ctxKey}`,
                            operations: []
                        }));
                    }
                }
            }
        }

        // KeyMissing ops: emitted after data-matched keys, in htmlJson template scan order.
        if (trackOperations && missingKeysInTemplateOrder && keyMissingTarget !== null) {
            for (const missingKey of missingKeysInTemplateOrder) {
                keyMissingTarget.push(new TemplateOperation({
                    templateName: templateKey,
                    kind: TemplateOperationKind.KeyMissing,
                    target: `{{$${missingKey}}}`,
                    replacedWith: `${currentTemplateName}_${missingKey}`,
                    operations: []
                }));
            }
        }

        return result;
    });
}

// Returns the data-source key casing when present (case-insensitive match).
// ReplacedWith/Target in ops use this canonical casing so JSON engines emit
// the same identifier as Normal/PreProcess (which iterate data-source keys).
export function tryGetCanonicalKey(dict: JsonObject | null, key: string): string {
    if (dict) {
        if (dict.has(key)) return key;
        const keyLower = key.toLowerCase();
        for (const existingKey of dict.keys()) {
            if (existingKey.toLowerCase() === keyLower) {
                return existingKey;
            }
        }
    }
    return key;
}

export function tryGetValueCaseInsensitive(dict: JsonObject, key: string): { found: boolean; value: JsonValue | null } {
    if (dict.has(key)) {
        return { found: true, value: dict.get(key) };
    }

    for (const [existingKey, existingValue] of dict.entries()) {
        if (existingKey.toLowerCase() === key.toLowerCase()) {
            return { found: true, value: existingValue };
        }
    }

    return { found: false, value: null };
}
